import { computed, ref, watch } from "vue";
import type { ComputedRef } from "vue";
import type { DSTabsItem, DSTabsProps } from "../types/props";

interface UseTabsSelectionOptions {
  props: Readonly<Pick<DSTabsProps, "disabled" | "items" | "modelValue">>;
  onSelect: (item: DSTabsItem) => void;
}

interface UseTabsSelectionResult {
  enabledItems: ComputedRef<DSTabsItem[]>;
  getItemTabIndex: (item: DSTabsItem) => number;
  isItemDisabled: (item: DSTabsItem) => boolean;
  isSelected: (item: DSTabsItem) => boolean;
  selectItem: (item: DSTabsItem) => void;
}

export const useTabsSelection = ({ props, onSelect }: UseTabsSelectionOptions): UseTabsSelectionResult => {
  const internalSelectedValue = ref<string | undefined>(props.modelValue);

  const isItemDisabled = (item: DSTabsItem): boolean => Boolean(props.disabled || item.disabled);

  const enabledItems = computed(() => props.items.filter(item => !isItemDisabled(item)));

  const selectedValue = computed(() => {
    const currentValue = internalSelectedValue.value;
    const currentItem = enabledItems.value.find(item => item.value === currentValue);

    return currentItem ? currentValue : enabledItems.value[0]?.value;
  });

  watch(
    () => props.modelValue,
    value => {
      internalSelectedValue.value = value;
    }
  );

  const isSelected = (item: DSTabsItem): boolean => item.value === selectedValue.value;

  const getItemTabIndex = (item: DSTabsItem): number => (isSelected(item) && !isItemDisabled(item) ? 0 : -1);

  const selectItem = (item: DSTabsItem) => {
    if (isItemDisabled(item)) return;

    internalSelectedValue.value = item.value;
    onSelect(item);
  };

  return {
    enabledItems,
    getItemTabIndex,
    isItemDisabled,
    isSelected,
    selectItem,
  };
};
